/**
 * RedprintValidator — Redprint-specific graph validation.
 *
 * Wraps the generic GraphValidator from @engine/graph and adds
 * Redprint-specific checks:
 * - Event reachability (impure nodes must be reachable from events)
 * - No event nodes warning
 * - Type compatibility using Redprint's type system
 */

import { Graph } from './base';
import { GraphValidator as BaseGraphValidator, topologicalSort } from '@engine/graph';
import type { ValidationResult, NodeDefinitionResolver } from '@engine/graph';
import type { NodeRegistry } from '../node/NodeRegistry';
import type { CompileDiagnostic, NodeInstance } from '../types';
import type { ConnectionCheck } from '../node/PortTypes';
import { canConnect, isFlowType } from '../node/PortTypes';

/**
 * Adapter to make NodeRegistry compatible with NodeDefinitionResolver.
 */
function createResolver(registry: NodeRegistry): NodeDefinitionResolver {
  return {
    has: (type: string) => registry.has(type),
    get: (type: string) => registry.get(type),
    resolveInputPort: (type: string, portId: string) => registry.resolveInputPort(type, portId),
    resolveOutputPort: (type: string, portId: string) => registry.resolveOutputPort(type, portId),
  };
}

/**
 * GraphValidator for Redprint visual scripting.
 */
export class GraphValidator {
  private readonly _registry: NodeRegistry;
  private readonly _baseValidator: BaseGraphValidator;

  constructor(registry: NodeRegistry) {
    this._registry = registry;
    const resolver = createResolver(registry);

    this._baseValidator = new BaseGraphValidator({
      registry: resolver,
      isFlowType: isFlowType,
      canConnect: (fromPort, toPort) => {
        const check: ConnectionCheck = canConnect(
          { ...fromPort, direction: 'output' },
          { ...toPort, direction: 'input' },
        );
        if (check.ok === true) {
          return { ok: true };
        }
        return { ok: false, reason: check.reason };
      },
    });
  }

  /**
   * Run all validation passes on a graph.
   */
  validate(graph: Graph): ValidationResult {
    const baseResult = this._baseValidator.validate(graph);
    const diags: CompileDiagnostic[] = [...baseResult.diagnostics];

    // Redprint-specific checks
    this._checkEventReachability(graph, diags);

    return {
      valid: diags.every(d => d.severity !== 'error'),
      diagnostics: diags,
    };
  }

  /**
   * Check that impure nodes are reachable from event nodes via flow edges.
   */
  private _checkEventReachability(graph: Graph, diags: CompileDiagnostic[]): void {
    const nodes = graph.getNodes() as NodeInstance[];

    // Find all event nodes
    const eventNodes = nodes.filter(n => {
      const def = this._registry.get(n.type);
      return def?.category === 'Events';
    });

    if (eventNodes.length === 0 && nodes.length > 0) {
      diags.push({
        severity: 'warning',
        message: 'Graph has no event nodes — nothing will execute',
      });
      return;
    }

    // Find all impure nodes (nodes with flow inputs that need to be triggered)
    const impureNodes = new Set<string>();
    for (const node of nodes) {
      const def = this._registry.get(node.type);
      if (!def) continue;

      // A node is impure if it has flow inputs and is not an event
      const hasFlowInput = def.inputs?.some(p => isFlowType(p.type));
      if (hasFlowInput && def.category !== 'Events') {
        impureNodes.add(node.id);
      }
    }

    if (impureNodes.size === 0) return;

    // BFS from event nodes following flow edges
    const reachable = new Set<string>();
    const queue: string[] = eventNodes.map(n => n.id);

    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      if (reachable.has(nodeId)) continue;
      reachable.add(nodeId);

      // Follow flow edges from this node
      const node = graph.getNode(nodeId);
      if (!node) continue;

      const def = this._registry.get(node.type);
      if (!def) continue;

      // Get all flow output ports
      const flowOutputs = def.outputs?.filter(p => isFlowType(p.type)) ?? [];

      for (const port of flowOutputs) {
        const edges = graph.getOutputEdges(nodeId, port.id);
        for (const edge of edges) {
          if (!reachable.has(edge.to.nodeId)) {
            queue.push(edge.to.nodeId);
          }
        }
      }
    }

    // Check which impure nodes are not reachable
    for (const nodeId of impureNodes) {
      if (!reachable.has(nodeId)) {
        const node = graph.getNode(nodeId);
        diags.push({
          severity: 'warning',
          message: `Node '${node?.type ?? nodeId}' is not reachable from any event`,
          nodeId,
        });
      }
    }
  }
}

/**
 * Topological sort of graph nodes based on data dependencies.
 * Returns array of node IDs in execution order.
 * Returns empty array if graph has cycles.
 */
export function topologicalSortData(graph: Graph, registry: NodeRegistry): string[] {
  const resolver = createResolver(registry);
  const sorted = topologicalSort(graph, resolver, isFlowType);

  if (sorted === null) {
    return [];
  }

  return sorted.map(n => n.id);
}
